import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-5 h-5" />
              <span>Home</span>
            </Link>
            <Link to="/" className="mx-auto">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
            </Link>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-8" data-testid="privacy-title">
          Privacy Policy
        </h1>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Boss Win is operated by <strong>Botelho Brothers Construction Ltd</strong> (Company No. 15154170),
              registered at 20 Elm Road, Thornton Heath, CR7 8RH, United Kingdom ("we", "us", "our").
              We are committed to protecting your personal data in accordance with the UK General Data Protection Regulation (UK GDPR)
              and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Data Controller</h2>
            <p className="text-gray-600 leading-relaxed">
              Botelho Brothers Construction Ltd is the data controller responsible for your personal data.
              If you have questions about this policy, contact us at: <strong>support@bosswin.store</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. What Data We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-2">We may collect the following personal data:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li><strong>Identity Data:</strong> Full name</li>
              <li><strong>Contact Data:</strong> Email address, phone number</li>
              <li><strong>Account Data:</strong> Login credentials (password stored securely hashed)</li>
              <li><strong>Transaction Data:</strong> Payment details processed by Stripe (we do not store card numbers)</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
              <li><strong>Usage Data:</strong> How you use our website and services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. How We Use Your Data</h2>
            <p className="text-gray-600 leading-relaxed mb-2">We use your personal data to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li>Administer free prize draws and process entries</li>
              <li>Process payments via our payment provider (Stripe)</li>
              <li>Contact winners and manage prize claims</li>
              <li>Send essential communications about your entries and account</li>
              <li>Comply with legal obligations</li>
              <li>Prevent fraud and ensure the security of our services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Legal Basis for Processing</h2>
            <p className="text-gray-600 leading-relaxed mb-2">We process your data based on:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li><strong>Contract:</strong> To fulfil our obligations when you enter a draw</li>
              <li><strong>Consent:</strong> Where you have given clear consent (e.g., marketing communications)</li>
              <li><strong>Legitimate Interest:</strong> To operate, improve, and secure our services</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Data Sharing</h2>
            <p className="text-gray-600 leading-relaxed">
              We do not sell your personal data. We may share data with:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2 mt-2">
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>Service providers:</strong> Who assist us in operating our website (hosting, email delivery)</li>
              <li><strong>Legal authorities:</strong> If required by law or to protect our rights</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-2">
              Winner names (first name and last initial) may be published on our website for draw transparency, as required by the Gambling Act 2005.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected,
              including to satisfy legal, accounting, or reporting requirements. Account data is retained while your account
              is active. Draw entry data is retained for a minimum of 2 years for audit purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-2">Under UK GDPR, you have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Restriction:</strong> Request limitation of data processing</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interest</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-2">
              To exercise any of these rights, contact us at <strong>support@bosswin.store</strong>.
              We will respond within one month.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              We use essential cookies to keep you logged in and to remember your preferences (such as
              acceptance of our terms). We do not use advertising or third-party tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">10. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate technical and organisational security measures to protect your personal data,
              including encrypted connections (HTTPS), secure password hashing, and access controls.
              Payment data is handled entirely by Stripe, which is PCI DSS Level 1 certified.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">11. Complaints</h2>
            <p className="text-gray-600 leading-relaxed">
              If you are unsatisfied with how we handle your data, you have the right to lodge a complaint with the
              Information Commissioner's Office (ICO): <a href="https://ico.org.uk" className="text-[#EAB308] hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">12. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this policy from time to time. Any changes will be posted on this page with an updated effective date.
              We encourage you to review this policy periodically.
            </p>
          </section>

          <div className="mt-12 p-4 bg-gray-100 rounded-xl text-sm text-gray-500" data-testid="privacy-update-note">
            <p>
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
            </p>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
